import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useLanguage } from '../context/LanguageContext'
import { useMicrocopy, calculateHoursSince, calculateDaysToRecord } from '../hooks/useMicrocopy'
import axios from 'axios'
import { cn } from '../lib/utils'

const API = `${process.env.REACT_APP_BACKEND_URL}/api`

// Bonsai SVG illustrations for each stage (minimalist Japanese style)
const BonsaiSVGs = {
  dead: (
    <svg viewBox="0 0 200 200" className="w-full h-full">
      {/* Pot */}
      <ellipse cx="100" cy="180" rx="45" ry="12" fill="#8B6914" />
      <rect x="55" y="155" width="90" height="25" rx="4" fill="#A67C00" />
      <ellipse cx="100" cy="155" rx="45" ry="10" fill="#C4A035" />
      {/* Dead trunk */}
      <path d="M100 155 L100 100 L95 80 L100 85 L105 75" stroke="#4a3728" strokeWidth="6" fill="none" strokeLinecap="round" opacity="0.6" />
      <path d="M100 120 L85 105" stroke="#4a3728" strokeWidth="4" fill="none" strokeLinecap="round" opacity="0.5" />
      {/* Wilted leaves on ground */}
      <ellipse cx="70" cy="152" rx="8" ry="3" fill="#6b5a3f" opacity="0.4" />
      <ellipse cx="130" cy="153" rx="6" ry="2" fill="#6b5a3f" opacity="0.3" />
    </svg>
  ),
  wilted: (
    <svg viewBox="0 0 200 200" className="w-full h-full">
      {/* Pot */}
      <ellipse cx="100" cy="180" rx="45" ry="12" fill="#8B6914" />
      <rect x="55" y="155" width="90" height="25" rx="4" fill="#A67C00" />
      <ellipse cx="100" cy="155" rx="45" ry="10" fill="#C4A035" />
      {/* Trunk */}
      <path d="M100 155 L100 95" stroke="#5d4a36" strokeWidth="8" strokeLinecap="round" />
      <path d="M100 120 L80 100" stroke="#5d4a36" strokeWidth="4" strokeLinecap="round" />
      <path d="M100 105 L120 90" stroke="#5d4a36" strokeWidth="3" strokeLinecap="round" />
      {/* Wilted drooping leaves */}
      <path d="M80 100 Q70 110 75 120" stroke="#8B9A6E" strokeWidth="3" fill="none" opacity="0.7" />
      <path d="M120 90 Q130 100 125 110" stroke="#8B9A6E" strokeWidth="2" fill="none" opacity="0.6" />
      <ellipse cx="100" cy="88" rx="12" ry="8" fill="#9BA885" opacity="0.5" />
      {/* Falling leaf */}
      <ellipse cx="135" cy="145" rx="5" ry="3" fill="#9BA885" opacity="0.4" className="animate-pulse" />
    </svg>
  ),
  sprout: (
    <svg viewBox="0 0 200 200" className="w-full h-full">
      {/* Pot */}
      <ellipse cx="100" cy="180" rx="45" ry="12" fill="#8B6914" />
      <rect x="55" y="155" width="90" height="25" rx="4" fill="#A67C00" />
      <ellipse cx="100" cy="155" rx="45" ry="10" fill="#C4A035" />
      {/* Small trunk */}
      <path d="M100 155 L100 115" stroke="#6d5a45" strokeWidth="6" strokeLinecap="round" />
      {/* Small sprout leaves */}
      <ellipse cx="90" cy="108" rx="15" ry="10" fill="#7CB342" transform="rotate(-20 90 108)" />
      <ellipse cx="110" cy="108" rx="15" ry="10" fill="#8BC34A" transform="rotate(20 110 108)" />
      <ellipse cx="100" cy="100" rx="10" ry="8" fill="#9CCC65" />
    </svg>
  ),
  young: (
    <svg viewBox="0 0 200 200" className="w-full h-full">
      {/* Pot */}
      <ellipse cx="100" cy="180" rx="45" ry="12" fill="#8B6914" />
      <rect x="55" y="155" width="90" height="25" rx="4" fill="#A67C00" />
      <ellipse cx="100" cy="155" rx="45" ry="10" fill="#C4A035" />
      {/* Trunk */}
      <path d="M100 155 L100 90" stroke="#6d5a45" strokeWidth="7" strokeLinecap="round" />
      <path d="M100 120 L80 105" stroke="#6d5a45" strokeWidth="4" strokeLinecap="round" />
      <path d="M100 100 L120 88" stroke="#6d5a45" strokeWidth="3" strokeLinecap="round" />
      {/* Foliage clusters */}
      <ellipse cx="75" cy="100" rx="18" ry="14" fill="#66BB6A" />
      <ellipse cx="120" cy="85" rx="15" ry="12" fill="#81C784" />
      <ellipse cx="100" cy="80" rx="22" ry="18" fill="#4CAF50" />
      <ellipse cx="90" cy="72" rx="12" ry="10" fill="#66BB6A" />
      <ellipse cx="110" cy="75" rx="10" ry="8" fill="#81C784" />
    </svg>
  ),
  healthy: (
    <svg viewBox="0 0 200 200" className="w-full h-full">
      {/* Pot */}
      <ellipse cx="100" cy="180" rx="45" ry="12" fill="#8B6914" />
      <rect x="55" y="155" width="90" height="25" rx="4" fill="#A67C00" />
      <ellipse cx="100" cy="155" rx="45" ry="10" fill="#C4A035" />
      {/* Main trunk */}
      <path d="M100 155 L100 80" stroke="#5d4a36" strokeWidth="8" strokeLinecap="round" />
      <path d="M100 130 L70 105" stroke="#5d4a36" strokeWidth="5" strokeLinecap="round" />
      <path d="M100 110 L130 90" stroke="#5d4a36" strokeWidth="4" strokeLinecap="round" />
      <path d="M100 95 L85 75" stroke="#5d4a36" strokeWidth="3" strokeLinecap="round" />
      {/* Lush foliage */}
      <ellipse cx="65" cy="100" rx="22" ry="18" fill="#43A047" />
      <ellipse cx="130" cy="85" rx="20" ry="16" fill="#4CAF50" />
      <ellipse cx="100" cy="70" rx="30" ry="22" fill="#388E3C" />
      <ellipse cx="80" cy="65" rx="18" ry="14" fill="#43A047" />
      <ellipse cx="120" cy="68" rx="16" ry="12" fill="#4CAF50" />
      <ellipse cx="100" cy="55" rx="15" ry="12" fill="#66BB6A" />
    </svg>
  ),
  flourishing: (
    <svg viewBox="0 0 200 200" className="w-full h-full">
      {/* Pot */}
      <ellipse cx="100" cy="180" rx="45" ry="12" fill="#8B6914" />
      <rect x="55" y="155" width="90" height="25" rx="4" fill="#A67C00" />
      <ellipse cx="100" cy="155" rx="45" ry="10" fill="#C4A035" />
      {/* Elegant trunk */}
      <path d="M100 155 L98 75" stroke="#4a3728" strokeWidth="9" strokeLinecap="round" />
      <path d="M100 135 L65 105" stroke="#4a3728" strokeWidth="5" strokeLinecap="round" />
      <path d="M100 115 L135 88" stroke="#4a3728" strokeWidth="4" strokeLinecap="round" />
      <path d="M100 95 L75 70" stroke="#4a3728" strokeWidth="3" strokeLinecap="round" />
      <path d="M98 80 L115 60" stroke="#4a3728" strokeWidth="3" strokeLinecap="round" />
      {/* Dense foliage with flowers */}
      <ellipse cx="60" cy="100" rx="25" ry="20" fill="#2E7D32" />
      <ellipse cx="140" cy="85" rx="22" ry="18" fill="#388E3C" />
      <ellipse cx="100" cy="65" rx="35" ry="25" fill="#1B5E20" />
      <ellipse cx="70" cy="60" rx="20" ry="16" fill="#2E7D32" />
      <ellipse cx="130" cy="62" rx="18" ry="14" fill="#388E3C" />
      <ellipse cx="100" cy="45" rx="20" ry="15" fill="#43A047" />
      {/* Small cherry blossoms */}
      <circle cx="55" cy="90" r="4" fill="#F8BBD9" />
      <circle cx="145" cy="80" r="3" fill="#F8BBD9" />
      <circle cx="85" cy="50" r="3" fill="#F8BBD9" />
      <circle cx="115" cy="55" r="4" fill="#F8BBD9" />
    </svg>
  ),
  masterpiece: (
    <svg viewBox="0 0 200 200" className="w-full h-full">
      {/* Decorative stand */}
      <rect x="40" y="185" width="120" height="8" rx="2" fill="#4a3728" />
      {/* Premium pot */}
      <ellipse cx="100" cy="180" rx="48" ry="14" fill="#1a4d3e" />
      <rect x="52" y="152" width="96" height="28" rx="5" fill="#236651" />
      <ellipse cx="100" cy="152" rx="48" ry="12" fill="#2d7d64" />
      {/* Gold trim */}
      <ellipse cx="100" cy="152" rx="48" ry="12" fill="none" stroke="#D4AF37" strokeWidth="2" />
      {/* Magnificent trunk */}
      <path d="M100 152 L95 65" stroke="#3d2b1f" strokeWidth="10" strokeLinecap="round" />
      <path d="M98 140 L55 100" stroke="#3d2b1f" strokeWidth="6" strokeLinecap="round" />
      <path d="M96 115 L145 80" stroke="#3d2b1f" strokeWidth="5" strokeLinecap="round" />
      <path d="M95 90 L65 55" stroke="#3d2b1f" strokeWidth="4" strokeLinecap="round" />
      <path d="M95 75 L125 50" stroke="#3d2b1f" strokeWidth="3" strokeLinecap="round" />
      {/* Perfect cloud-like foliage */}
      <ellipse cx="50" cy="95" rx="28" ry="22" fill="#1B5E20" />
      <ellipse cx="150" cy="75" rx="25" ry="20" fill="#2E7D32" />
      <ellipse cx="100" cy="55" rx="40" ry="30" fill="#145214" />
      <ellipse cx="60" cy="50" rx="22" ry="18" fill="#1B5E20" />
      <ellipse cx="140" cy="52" rx="20" ry="16" fill="#2E7D32" />
      <ellipse cx="100" cy="30" rx="25" ry="18" fill="#388E3C" />
      <ellipse cx="80" cy="35" rx="15" ry="12" fill="#43A047" />
      <ellipse cx="120" cy="38" rx="14" ry="11" fill="#43A047" />
      {/* Cherry blossoms in full bloom */}
      <circle cx="45" cy="85" r="5" fill="#FFB7C5" />
      <circle cx="155" cy="70" r="4" fill="#FFB7C5" />
      <circle cx="75" cy="40" r="4" fill="#FFB7C5" />
      <circle cx="125" cy="45" r="5" fill="#FFB7C5" />
      <circle cx="100" cy="22" r="4" fill="#FFB7C5" />
      <circle cx="60" cy="55" r="3" fill="#FFB7C5" />
      <circle cx="140" cy="58" r="3" fill="#FFB7C5" />
      {/* Sparkle effect */}
      <circle cx="30" cy="40" r="2" fill="#FFD700" className="animate-pulse" />
      <circle cx="170" cy="50" r="2" fill="#FFD700" className="animate-pulse" style={{ animationDelay: '0.5s' }} />
      <circle cx="100" cy="15" r="2" fill="#FFD700" className="animate-pulse" style={{ animationDelay: '1s' }} />
    </svg>
  )
}

// Stage info with colors and descriptions
const stageInfo = {
  dead: { 
    color: 'from-gray-600 to-gray-800', 
    bgColor: 'bg-gray-900/50',
    textColor: 'text-gray-400',
    es: { name: 'Muerto', desc: 'Tu bonsái necesita atención urgente' },
    en: { name: 'Dead', desc: 'Your bonsai needs urgent attention' }
  },
  wilted: { 
    color: 'from-yellow-700 to-amber-900', 
    bgColor: 'bg-amber-950/50',
    textColor: 'text-amber-300',
    es: { name: 'Marchito', desc: 'Regístralo pronto para recuperarlo' },
    en: { name: 'Wilted', desc: 'Log soon to recover it' }
  },
  sprout: { 
    color: 'from-lime-600 to-green-700', 
    bgColor: 'bg-lime-950/50',
    textColor: 'text-lime-300',
    es: { name: 'Brote', desc: 'Un nuevo comienzo prometedor' },
    en: { name: 'Sprout', desc: 'A promising new beginning' }
  },
  young: { 
    color: 'from-green-500 to-emerald-700', 
    bgColor: 'bg-emerald-950/50',
    textColor: 'text-emerald-300',
    es: { name: 'Joven', desc: 'Creciendo con buen ritmo' },
    en: { name: 'Young', desc: 'Growing at a good pace' }
  },
  healthy: { 
    color: 'from-emerald-500 to-teal-700', 
    bgColor: 'bg-teal-950/50',
    textColor: 'text-teal-200',
    es: { name: 'Saludable', desc: 'Tu constancia da frutos' },
    en: { name: 'Healthy', desc: 'Your consistency bears fruit' }
  },
  flourishing: { 
    color: 'from-teal-400 to-cyan-600', 
    bgColor: 'bg-cyan-950/50',
    textColor: 'text-cyan-200',
    es: { name: 'Floreciente', desc: '¡Impresionante dedicación!' },
    en: { name: 'Flourishing', desc: 'Impressive dedication!' }
  },
  masterpiece: { 
    color: 'from-amber-400 via-yellow-500 to-amber-600', 
    bgColor: 'bg-gradient-to-br from-amber-950/60 to-yellow-900/40',
    textColor: 'text-amber-200',
    es: { name: 'Obra Maestra', desc: '¡Perfección alcanzada!' },
    en: { name: 'Masterpiece', desc: 'Perfection achieved!' }
  }
}

export default function BonsaiCard() {
  const { locale } = useLanguage()
  const lang = locale.startsWith('es') ? 'es' : 'en'
  
  const [bonsaiData, setBonsaiData] = useState(null)
  const [streakData, setStreakData] = useState({ streakDays: 0, personalBest: 0 })
  const [todayProgress, setTodayProgress] = useState({ completed: 0, total: 0 })
  const [loading, setLoading] = useState(true)
  const [animating, setAnimating] = useState(false)
  const [animationType, setAnimationType] = useState(null) // 'grow' or 'decay'
  const prevScoreRef = useRef(null)

  // Memoize calculated values to prevent unnecessary re-renders
  // Only recalculate when bonsaiData.last_activity_at actually changes
  const hoursSinceLastOpen = useMemo(() => {
    return calculateHoursSince(bonsaiData?.last_activity_at)
  }, [bonsaiData?.last_activity_at])
  
  const daysToRecord = useMemo(() => {
    return calculateDaysToRecord(streakData.streakDays, streakData.personalBest)
  }, [streakData.streakDays, streakData.personalBest])
  
  // Get dynamic microcopy - now stable because hoursSinceLastOpen is memoized
  const { headline, subline, group } = useMicrocopy({
    userLevelScore: bonsaiData?.score ?? 0,
    streakDays: streakData.streakDays,
    hoursSinceLastOpen,
    todayCompleted: todayProgress.completed,
    todayTotal: todayProgress.total,
    daysToRecord
  })

  const fetchBonsaiStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/bonsai/status`)
      const data = response.data
      
      // Check for score change to trigger animation
      if (prevScoreRef.current !== null && prevScoreRef.current !== data.score) {
        const delta = data.score - prevScoreRef.current
        setAnimationType(delta > 0 ? 'grow' : 'decay')
        setAnimating(true)
        setTimeout(() => setAnimating(false), 1500)
      }
      
      prevScoreRef.current = data.score
      setBonsaiData(data)
      
      // Extract streak data if available
      if (data.streak_days !== undefined) {
        setStreakData(prev => ({
          ...prev,
          streakDays: data.streak_days,
          personalBest: data.personal_best_streak || prev.personalBest
        }))
      }
    } catch (error) {
      console.error('Error fetching bonsai status:', error)
      // If 404 or no data, trigger migration
      if (error.response?.status === 404 || !bonsaiData) {
        try {
          await axios.post(`${API}/bonsai/migrate`)
          // Retry fetch
          const retryResponse = await axios.get(`${API}/bonsai/status`)
          setBonsaiData(retryResponse.data)
          prevScoreRef.current = retryResponse.data.score
        } catch (migrateError) {
          console.error('Error migrating bonsai:', migrateError)
        }
      }
    } finally {
      setLoading(false)
    }
  }, [bonsaiData])

  // Fetch today's progress for microcopy context
  const fetchTodayProgress = useCallback(async () => {
    try {
      const today = new Date().toISOString().split('T')[0]
      const response = await axios.get(`${API}/dailylog/${today}`)
      if (response.data) {
        const total = response.data.categories?.reduce((sum, cat) => sum + (cat.items?.length || 0), 0) || 0
        const completed = response.data.categories?.reduce((sum, cat) => 
          sum + (cat.items?.filter(item => item.done)?.length || 0), 0) || 0
        setTodayProgress({ completed, total })
      }
    } catch (error) {
      // Silently fail - not critical
    }
  }, [])

  useEffect(() => {
    fetchBonsaiStatus()
    fetchTodayProgress()
    
    // Poll every 30 seconds to catch updates
    const interval = setInterval(fetchBonsaiStatus, 30000)
    return () => clearInterval(interval)
  }, [fetchBonsaiStatus, fetchTodayProgress])

  // Listen for custom events when activities happen
  useEffect(() => {
    const handleBonsaiUpdate = () => {
      setTimeout(fetchBonsaiStatus, 500) // Small delay to let backend process
    }
    
    window.addEventListener('bonsai-update', handleBonsaiUpdate)
    return () => window.removeEventListener('bonsai-update', handleBonsaiUpdate)
  }, [fetchBonsaiStatus])

  if (loading) {
    return (
      <div className="bg-card rounded-2xl p-4 animate-pulse">
        <div className="h-32 bg-muted rounded-xl" />
      </div>
    )
  }

  if (!bonsaiData) {
    return null
  }

  const stage = bonsaiData.stage || 'healthy'
  const info = stageInfo[stage] || stageInfo.healthy
  const BonsaiSVG = BonsaiSVGs[stage] || BonsaiSVGs.healthy

  return (
    <div 
      className={cn(
        "relative rounded-2xl overflow-hidden transition-all duration-500",
        info.bgColor,
        "border border-white/10"
      )}
      data-testid="bonsai-card"
    >
      {/* Background gradient */}
      <div className={cn(
        "absolute inset-0 bg-gradient-to-br opacity-30",
        info.color
      )} />
      
      {/* Content */}
      <div className="relative p-4 flex items-center gap-4">
        {/* Bonsai illustration */}
        <div 
          className={cn(
            "relative w-24 h-24 md:w-28 md:h-28 flex-shrink-0 transition-all duration-700",
            animating && animationType === 'grow' && "scale-110",
            animating && animationType === 'decay' && "scale-95 opacity-80"
          )}
        >
          {/* Glow effect for high stages */}
          {['flourishing', 'masterpiece'].includes(stage) && (
            <div className="absolute inset-0 bg-gradient-radial from-green-400/20 to-transparent blur-xl" />
          )}
          
          {/* SVG container with animation */}
          <div className={cn(
            "relative z-10 transition-transform duration-700",
            animating && animationType === 'grow' && "animate-bounce"
          )}>
            {BonsaiSVG}
          </div>
          
          {/* Growing particles animation */}
          {animating && animationType === 'grow' && (
            <div className="absolute inset-0 pointer-events-none">
              {[...Array(6)].map((_, i) => (
                <div
                  key={i}
                  className="absolute w-2 h-2 bg-green-400 rounded-full animate-ping"
                  style={{
                    left: `${30 + Math.random() * 40}%`,
                    top: `${20 + Math.random() * 30}%`,
                    animationDelay: `${i * 0.1}s`,
                    animationDuration: '0.8s'
                  }}
                />
              ))}
            </div>
          )}
          
          {/* Falling leaves animation for decay */}
          {animating && animationType === 'decay' && (
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
              {[...Array(4)].map((_, i) => (
                <div
                  key={i}
                  className="absolute w-3 h-2 bg-amber-600/70 rounded-full"
                  style={{
                    left: `${20 + i * 20}%`,
                    animation: 'fall 1s ease-in forwards',
                    animationDelay: `${i * 0.15}s`
                  }}
                />
              ))}
            </div>
          )}
        </div>
        
        {/* Info section */}
        <div className="flex-1 min-w-0">
          {/* Stage name */}
          <h3 className={cn(
            "text-lg font-bold tracking-wide",
            info.textColor
          )}>
            {info[lang].name}
          </h3>
          
          {/* Score bar */}
          <div className="mt-2 mb-1">
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-white/70">
                {lang === 'es' ? 'Vitalidad' : 'Vitality'}
              </span>
              <span className={cn("font-mono font-bold", info.textColor)}>
                {bonsaiData.score}/100
              </span>
            </div>
            <div className="h-2 bg-black/30 rounded-full overflow-hidden">
              <div 
                className={cn(
                  "h-full rounded-full bg-gradient-to-r transition-all duration-1000",
                  info.color
                )}
                style={{ width: `${bonsaiData.score}%` }}
              />
            </div>
          </div>
          
          {/* Dynamic Microcopy - Headline */}
          <p className={cn(
            "text-sm font-semibold mt-2 transition-opacity duration-300",
            info.textColor
          )}>
            {headline}
          </p>
          
          {/* Dynamic Microcopy - Subline */}
          {subline && (
            <p className="text-xs text-white/60 mt-0.5 transition-opacity duration-300">
              {subline}
            </p>
          )}
          
          {/* Days inactive warning - only show if not in comeback mode */}
          {bonsaiData.days_inactive > 0 && group !== 'COMEBACK' && (
            <p className={cn(
              "text-xs mt-1 font-medium",
              bonsaiData.days_inactive >= 3 ? "text-red-400" : "text-amber-400"
            )}>
              {lang === 'es' 
                ? `${bonsaiData.days_inactive} día${bonsaiData.days_inactive > 1 ? 's' : ''} sin actividad`
                : `${bonsaiData.days_inactive} day${bonsaiData.days_inactive > 1 ? 's' : ''} inactive`
              }
            </p>
          )}
        </div>
      </div>
      
      {/* Custom CSS for falling animation */}
      <style>{`
        @keyframes fall {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(80px) rotate(180deg); opacity: 0; }
        }
      `}</style>
    </div>
  )
}
